// apps/client/src/pages/reports/Reports.tsx
import {
  AllocationStatus,
  ProjectAccount,
  ResearchActivity,
  ResearchActivityType,
  TransactionWithAllocations,
  UserRole,
} from '@avanzu/types';
import { DeleteOutline, ExpandLess, ExpandMore, ScienceOutlined } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import PersonPercentIcon from './components/PersonPercentIcon';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EmptyStateContent } from '../../components/EmptyStateContent';
import HelpComponent from '../../components/HelpComponent';
import { LoadingComponent } from '../../components/LoadingComponent';
import PaperSection from '../../components/PaperSection';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import { ContentWrapper } from '../../layout/ContentWrapper';
import { apiClient } from '../../services/apiClient';
import { fetchProjectActivities } from '../../services/projects';
import { fetchProjectSalaryMixStatus, ProjectSalaryMixStatus } from '../../services/salaries';
import { removeTransactionAllocations } from '../../services/transactions';
import formatCurrency from '../../utils/formatCurrency';
import formatDate, { DateFormatOptions } from '../../utils/formatDate';

export const ReportPageRoles = [
  UserRole.AVANZU,
  UserRole.OWNER,
  UserRole.ADMIN,
  UserRole.MEMBER,
  UserRole.VIEWER,
];

interface AccountGroup {
  account: ProjectAccount;
  transactionCount: number;
  totalAmount: number;
  allocatedAmount: number;
  activityTotals: Record<string, number>; // Activity ID -> total allocated amount
  transactions?: TransactionWithAllocations[];
  isLoading?: boolean;
  isExpanded?: boolean;
}

export const Reports: React.FC = () => {
  const [activities, setActivities] = useState<ResearchActivity[]>([]);
  const [accountGroups, setAccountGroups] = useState<AccountGroup[]>([]);
  const [showEmptyAccounts, setShowEmptyAccounts] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Salary-mix freshness — polled every 5s while the page is visible so the
  // user can see when an async recompute has caught up after a salary edit.
  const [mixStatus, setMixStatus] = useState<ProjectSalaryMixStatus | null>(null);
  const { organisationId } = useAuth();
  const { currentProject, refreshCurrentProject } = useProject();
  const projectId = currentProject?.id;
  const navigate = useNavigate();

  // Fetch project accounts. The backend filters by the org's
  // allowedAccountTypes setting (default: [EXPENSE]) so no client-side
  // type filter is needed here.
  const fetchProjectAccounts = async (organisationId: string, projectId: string) => {
    try {
      const params = new URLSearchParams();
      params.append('allocatedOnly', 'false');
      const url = `/organisations/${organisationId}/projects/${projectId}/accounts?${params.toString()}`;
      const response = await apiClient.get(url);

      if (!response.ok) {
        throw new Error('Failed to fetch project accounts');
      }

      const data = await response.json();
      return data.accounts as ProjectAccount[];
    } catch (error) {
      console.error('Error fetching project accounts:', error);
      throw error;
    }
  };

  // Fetch transactions for a specific account
  const fetchAccountTransactions = async (
    organisationId: string,
    projectId: string,
    accountId: string
  ) => {
    try {
      const response = await apiClient.get(
        `/organisations/${organisationId}/projects/${projectId}/accounts/${accountId}/transactions`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch account transactions');
      }

      const data = await response.json();
      return data.transactions as TransactionWithAllocations[];
    } catch (error) {
      console.error('Error fetching account transactions:', error);
      throw error;
    }
  };

  const convertToAccountGroup = (projectAccount: ProjectAccount): AccountGroup => {
    const activityTotals: Record<string, number> = {};

    projectAccount.allocations?.forEach((allocation) => {
      // Use the amount directly from the backend instead of recalculating
      if (allocation.amount !== undefined) {
        activityTotals[allocation.activityId] = allocation.amount;
      }
    });

    return {
      account: projectAccount,
      transactionCount: projectAccount.transactionCount || 0,
      totalAmount: projectAccount.totalLineAmount || 0,
      allocatedAmount: projectAccount.allocatedAmount || 0,
      activityTotals,
      isExpanded: false,
      isLoading: false,
      transactions: undefined,
    };
  };

  const loadInitialData = async (organisationId: string, projectId: string) => {
    try {
      setLoading(true);
      setError(null);

      // Fetch activities and project accounts in parallel
      const [activitiesData, projectAccounts] = await Promise.all([
        fetchProjectActivities(organisationId, projectId),
        fetchProjectAccounts(organisationId, projectId),
      ]);

      setActivities(activitiesData.activities);

      // Convert project accounts to account groups using server-side data
      const accountGroupsWithSummaries = projectAccounts.map(convertToAccountGroup);

      setAccountGroups(accountGroupsWithSummaries);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
      console.error('Error loading reports data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!projectId || !organisationId) return;
    loadInitialData(organisationId, projectId);
  }, [projectId, organisationId]);

  // Poll the salary-mix freshness while the page is visible. Cheap single-row
  // read; stops when the user navigates away (cleanup via interval id).
  useEffect(() => {
    if (!projectId || !organisationId) return;

    let cancelled = false;
    const poll = async () => {
      try {
        const status = await fetchProjectSalaryMixStatus(organisationId, projectId);
        if (!cancelled) setMixStatus(status);
      } catch {
        // Swallow — chip just disappears on transient errors.
      }
    };

    poll();
    const id = setInterval(poll, 5000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [projectId, organisationId]);

  // Filter account groups based on showEmptyAccounts toggle
  const filteredAccountGroups = showEmptyAccounts
    ? accountGroups
    : accountGroups.filter((group) => group.transactionCount > 0);

  const sortedActivities = useMemo(() => {
    return [...activities].sort((a, b) => {
      return a.sortOrder - b.sortOrder;
    });
  }, [activities]);
  const handleActivityDelete = async (transactionId: string) => {
    if (!organisationId) return;

    try {
      const response = await removeTransactionAllocations(organisationId, transactionId);

      if (response.success) {
        // Remove the transaction from local state and update totals
        setAccountGroups((prev) =>
          prev.map((group) => {
            // Check if this group contains the transaction we're deleting
            const transactionToDelete = group.transactions?.find(
              (t) => t.transaction.id === transactionId
            );

            if (transactionToDelete) {
              const transactionAmount = transactionToDelete.transaction.lineAmount;
              const isExcluded =
                transactionToDelete.transaction.allocationStatus === AllocationStatus.EXCLUDED;

              // Only update totals if transaction was not excluded
              let updatedActivityTotals = { ...group.activityTotals };
              let totalAllocatedToRemove = 0;

              if (!isExcluded) {
                // Update activity totals by subtracting allocated amounts
                transactionToDelete.allocations.forEach((allocation) => {
                  const allocatedAmount = (transactionAmount * allocation.allocation) / 100;
                  updatedActivityTotals[allocation.activityId] =
                    (updatedActivityTotals[allocation.activityId] || 0) - allocatedAmount;
                  totalAllocatedToRemove += allocatedAmount;
                });
              }

              return {
                ...group,
                transactions: group.transactions?.filter((t) => t.transaction.id !== transactionId),
                transactionCount: Math.max(0, group.transactionCount - 1),
                totalAmount: group.totalAmount - transactionAmount,
                allocatedAmount: isExcluded
                  ? group.allocatedAmount
                  : group.allocatedAmount - totalAllocatedToRemove,
                activityTotals: updatedActivityTotals,
              };
            }

            return group;
          })
        );

        // Refresh project to update transaction count
        await refreshCurrentProject();
      }
    } catch (error) {
      console.error('Error deleting transaction:', error);
      setError('Failed to delete transaction allocation');
    }
  };

  const toggleAccountExpansion = async (accountId: string) => {
    if (!organisationId || !projectId) return;

    const targetGroup = accountGroups.find((g) => g.account.id === accountId);
    if (!targetGroup) return;

    // If already expanded, just collapse
    if (targetGroup.isExpanded) {
      setAccountGroups((prev) =>
        prev.map((group) => {
          if (group.account.id !== accountId) return group;
          return {
            ...group,
            isExpanded: false,
          };
        })
      );
      return;
    }

    // If expanding and we already have transactions, just expand
    if (targetGroup.transactions) {
      setAccountGroups((prev) =>
        prev.map((group) => {
          if (group.account.id !== accountId) return group;
          return {
            ...group,
            isExpanded: true,
          };
        })
      );
      return;
    }

    // Need to load transactions
    setAccountGroups((prev) =>
      prev.map((group) => {
        if (group.account.id !== accountId) return group;
        return {
          ...group,
          isLoading: true,
          isExpanded: true,
        };
      })
    );

    try {
      const transactions = await fetchAccountTransactions(organisationId, projectId, accountId);

      // Calculate activity totals from the loaded transactions
      const activityTotals: Record<string, number> = {};
      activities.forEach((activity) => {
        activityTotals[activity.id] = 0;
      });

      transactions.forEach((transactionWithAlloc) => {
        transactionWithAlloc.allocations.forEach((allocation) => {
          const amount =
            (transactionWithAlloc.transaction.lineAmount * allocation.allocation) / 100;
          activityTotals[allocation.activityId] =
            (activityTotals[allocation.activityId] || 0) + amount;
        });
      });

      setAccountGroups((prev) =>
        prev.map((group) => {
          if (group.account.id !== accountId) return group;
          return {
            ...group,
            transactions,
            activityTotals,
            isLoading: false,
            isExpanded: true,
          };
        })
      );
    } catch (error) {
      console.error('Error loading account transactions:', error);
      setAccountGroups((prev) =>
        prev.map((group) => {
          if (group.account.id !== accountId) return group;
          return {
            ...group,
            isLoading: false,
            isExpanded: false, // Collapse on error
          };
        })
      );
      setError('Failed to load transactions for this account');
    }
  };

  const getActivityAllocation = (
    transaction: TransactionWithAllocations,
    activityId: string
  ): number => {
    const allocation = transaction.allocations.find((a) => a.activityId === activityId);
    return allocation ? allocation.allocation : 0;
  };

  // Helper function to get row styling based on allocation status
  const getTransactionRowSx = (allocationStatus: AllocationStatus) => {
    const baseSx = {
      '&:hover': { bgcolor: 'action.hover' },
    };

    if (allocationStatus === AllocationStatus.EXCLUDED) {
      return {
        ...baseSx,
        opacity: 0.5,
        '& .MuiTableCell-root': {
          color: 'text.disabled',
        },
      };
    }

    return baseSx;
  };

  // Human-readable "x seconds/minutes ago" for the salary-mix freshness chip.
  // Kept inline (rather than a util) because it's the only consumer right now.
  const formatRelativeTime = (iso: string): string => {
    const diffMs = Date.now() - new Date(iso).getTime();
    if (diffMs < 0) return 'just now';
    const secs = Math.floor(diffMs / 1000);
    if (secs < 5) return 'just now';
    if (secs < 60) return `${secs}s ago`;
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  // Loading skeleton for transactions
  const TransactionLoadingSkeleton = () => (
    <TableRow>
      <TableCell></TableCell>
      <TableCell colSpan={3 + sortedActivities.length}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} sx={{ mr: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Loading transactions...
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );

  if (loading) {
    return (
      <ContentWrapper>
        <PaperSection title="Reports">
          <LoadingComponent context="Loading report data..." />
        </PaperSection>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper>
      <PaperSection
        title="Transaction Allocations Report"
        action={
          <>
            {mixStatus?.hasMix && mixStatus.computedAt && (
              <Tooltip
                title={`Salary mix last recomputed at ${new Date(
                  mixStatus.computedAt
                ).toLocaleString()}. Rows derived via salary loading reflect this snapshot.`}
              >
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                  Salary allocations last updated {formatRelativeTime(mixStatus.computedAt)}
                </Typography>
              </Tooltip>
            )}
            <Button variant="text" onClick={() => navigate(`/app/transactions/${projectId}`)}>
              Allocate Transactions
            </Button>

            <HelpComponent title="Review allocated transactions by account.">
              <Typography variant="body2" paragraph>
                This report groups every confirmed transaction by account and shows how its cost has
                been distributed across research activities. Use it to audit allocations before
                finalising your R&D claim, or to spot accounts that still need attention.
              </Typography>
              <Typography variant="body2" paragraph>
                Rows derived from salary loading reflect the most recent salary mix snapshot (see
                the timestamp above). Toggle <strong>Show empty accounts</strong> to include accounts
                with no allocated transactions. To re-allocate a transaction, remove it here to
                return it to the pending list on the Transactions page.
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Stack spacing={0.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <DeleteOutline sx={{ fontSize: '16px', color: 'secondary.main' }} />
                  <Typography variant="caption">
                    Removes transaction allocations and returns transaction to pending transactions
                    list
                  </Typography>
                </Stack>
              </Stack>
            </HelpComponent>
          </>
        }
      >
        <Box sx={{ mb: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Filter Controls */}
          {filteredAccountGroups.length > 0 && (
            <Box
              sx={{
                mt: -2,
                mb: 0,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={showEmptyAccounts}
                    onChange={(e) => setShowEmptyAccounts(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    Show all accounts
                  </Typography>
                }
              />

              <Typography variant="body2" color="text.secondary">
                Showing {filteredAccountGroups.length} of {accountGroups.length} accounts
              </Typography>
            </Box>
          )}
        </Box>

        {filteredAccountGroups.length === 0 ? (
          <Box sx={{ my: 10 }}>
            <EmptyStateContent
              title="No allocated transactions"
              message={
                showEmptyAccounts
                  ? 'There are no accounts to display.'
                  : 'There are no accounts with transactions to display. You need to allocate transactions first.'
              }
              icon={<ScienceOutlined />}
              button={
                <Button
                  variant="contained"
                  onClick={() => navigate(`/app/transactions/${projectId}`)}
                >
                  Allocate your first transaction
                </Button>
              }
              showBorder={false}
            />
          </Box>
        ) : (
          <TableContainer
            component={Box}
            sx={{
              mx: -6,
              px: 6,
              mb: -6,
              width: 'calc(100% + 96px)',
              overflowX: 'scroll',
              '&::-webkit-scrollbar': {
                WebkitAppearance: 'none',
                width: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: '4px',
                backgroundColor: 'rgba(0, 0, 0, .3)',
              },
            }}
          >
            <Table stickyHeader size="small">
              <TableHead
                sx={{
                  '& .MuiTableCell-head': {
                    zIndex: (theme) => theme.zIndex.appBar + 1,
                    backgroundColor: 'background.paper',
                    borderColor: 'divider',
                    borderTop: '0px none',
                  },
                }}
              >
                <TableRow>
                  <TableCell sx={{ width: 40, minWidth: 40 }}></TableCell>
                  <TableCell sx={{ width: 300, minWidth: 300, fontWeight: 600 }}>
                    Account / Transaction
                  </TableCell>
                  <TableCell sx={{ width: 100, minWidth: 100, fontWeight: 600 }}>Amount</TableCell>
                  <TableCell sx={{ width: 120, minWidth: 120, fontWeight: 600 }}>Date</TableCell>
                  {sortedActivities.map((activity) => (
                    <TableCell
                      key={`activity-${activity.id}`}
                      sx={{ width: 100, minWidth: 100, p: 1, verticalAlign: 'top' }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <Chip
                          label={activity.type === ResearchActivityType.CORE ? 'CORE' : 'SUPP'}
                          size="small"
                          color={activity.type === ResearchActivityType.CORE ? 'info' : 'primary'}
                          sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                        />
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          align="center"
                          sx={{ lineHeight: 1.2 }}
                        >
                          {activity.name}
                        </Typography>
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell colSpan={4}></TableCell>
                  {sortedActivities.map((activity) => (
                    <TableCell key={`summary-${activity.id}`} align="center">
                      <Typography variant="caption" color="text.secondary">
                        {formatCurrency(activity.actualAmount || 0)}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAccountGroups.length > 0 &&
                  filteredAccountGroups.map((group) => (
                    <React.Fragment key={group.account.id}>
                      {/* Account Group Header */}
                      <TableRow
                        sx={{
                          bgcolor: 'grey.50',
                          cursor: group.transactionCount > 0 ? 'pointer' : 'default',
                          '&:hover': group.transactionCount > 0 ? { bgcolor: 'grey.100' } : {},
                        }}
                        onClick={
                          group.transactionCount > 0
                            ? () => toggleAccountExpansion(group.account.id)
                            : undefined
                        }
                      >
                        <TableCell>
                          {group.transactionCount > 0 && (
                            <IconButton size="small">
                              {group.isExpanded ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {group.account.code} - {group.account.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {group.transactionCount} transaction
                            {group.transactionCount !== 1 ? 's' : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {formatCurrency(group.totalAmount)}
                          </Typography>
                        </TableCell>
                        <TableCell></TableCell>
                        {sortedActivities.map((activity) => {
                          const totalAllocatedAmount = group.activityTotals?.[activity.id] || 0;

                          return (
                            <TableCell key={activity.id} sx={{ textAlign: 'center' }}>
                              {totalAllocatedAmount > 0 && (
                                <Typography variant="caption" fontWeight={500} color="text.primary">
                                  {formatCurrency(totalAllocatedAmount)}
                                </Typography>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>

                      {/* Loading State */}
                      {group.isExpanded && group.isLoading && <TransactionLoadingSkeleton />}

                      {/* Transaction Details */}
                      {group.isExpanded &&
                        !group.isLoading &&
                        group.transactions &&
                        group.transactions.map((transactionWithAlloc) => (
                          <TableRow
                            key={transactionWithAlloc.transaction.id}
                            sx={getTransactionRowSx(
                              transactionWithAlloc.transaction.allocationStatus
                            )}
                          >
                            <TableCell></TableCell>
                            <TableCell sx={{}}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  sx={{
                                    '&:hover': {
                                      color: 'error.main',
                                    },
                                  }}
                                  onClick={() =>
                                    handleActivityDelete(transactionWithAlloc.transaction.id)
                                  }
                                >
                                  <DeleteOutline fontSize="small" />
                                </IconButton>
                                <Typography variant="body2" sx={{ mb: 0.25, fontSize: '0.75rem' }}>
                                  {transactionWithAlloc.transaction.description || 'No description'}{' '}
                                  <Box component="span" sx={{ color: 'text.secondary' }}>
                                    ({transactionWithAlloc.transaction.contactName || 'No contact'})
                                  </Box>
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                {formatCurrency(
                                  transactionWithAlloc.transaction.totalAmount -
                                    (transactionWithAlloc.transaction.taxAmount || 0)
                                )}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: '0.75rem' }}
                              >
                                {formatDate(
                                  new Date(transactionWithAlloc.transaction.date),
                                  DateFormatOptions.DATEONLY
                                )}
                              </Typography>
                            </TableCell>
                            {sortedActivities.map((activity) => {
                              const allocation = getActivityAllocation(
                                transactionWithAlloc,
                                activity.id
                              );
                              const isSalaryLoaded =
                                transactionWithAlloc.transaction.applySalaryLoading;
                              return (
                                <TableCell key={activity.id} sx={{ textAlign: 'center' }}>
                                  <Stack
                                    direction="row"
                                    spacing={0.5}
                                    justifyContent="center"
                                    alignItems="center"
                                  >
                                    <Typography
                                      variant="body2"
                                      fontWeight={500}
                                      color="text.secondary"
                                      sx={{ fontSize: '0.75rem' }}
                                    >
                                      {allocation > 0 ? `${allocation}%` : '-'}
                                    </Typography>
                                    {isSalaryLoaded && allocation > 0 && (
                                      <Tooltip title="Derived from salary loading">
                                        <PersonPercentIcon
                                          sx={{
                                            fontSize: '14px',
                                            color: 'primary.main',
                                            opacity: 0.7,
                                          }}
                                        />
                                      </Tooltip>
                                    )}
                                  </Stack>
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))}
                    </React.Fragment>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </PaperSection>
    </ContentWrapper>
  );
};

export default Reports;
